/* eslint-disable react-refresh/only-export-components */
import {
  createBrowserRouter,
  isRouteErrorResponse,
  Link,
  Navigate,
  useRouteError,
} from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Layout from '@/components/layout/Layout'
import ProtectedRoute from '@/components/guards/ProtectedRoute'
import ModuleRoute from '@/components/guards/ModuleRoute'
import ConsentGate from '@/components/compliance/ConsentGate'
import CookieConsentBanner from '@/components/compliance/CookieConsentBanner'
import LoginPage from '@/pages/auth/LoginPage'
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage'
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage'
import DashboardPage from '@/pages/DashboardPage'
import ClientsListPage from '@/pages/clients/ClientsListPage'
import ClientDetailPage from '@/pages/clients/ClientDetailPage'
import AgendaPage from '@/pages/jobs/AgendaPage'
import JobFormPage from '@/pages/jobs/JobFormPage'
import JobDetailPage from '@/pages/jobs/JobDetailPage'
import JobReportWizardPage from '@/pages/jobs/JobReportWizardPage'
import QuotesListPage from '@/pages/quotes/QuotesListPage'
import QuoteFormPage from '@/pages/quotes/QuoteFormPage'
import QuoteDetailPage from '@/pages/quotes/QuoteDetailPage'
import InvoicesListPage from '@/pages/invoices/InvoicesListPage'
import InvoiceFormPage from '@/pages/invoices/InvoiceFormPage'
import InvoiceDetailPage from '@/pages/invoices/InvoiceDetailPage'
import CreditNotesListPage from '@/pages/invoices/CreditNotesListPage'
import PaymentsListPage from '@/pages/payments/PaymentsListPage'
import PaymentFormPage from '@/pages/payments/PaymentFormPage'
import PaymentDetailPage from '@/pages/payments/PaymentDetailPage'
import ContractsListPage from '@/pages/contracts/ContractsListPage'
import ContractFormPage from '@/pages/contracts/ContractFormPage'
import ContractDetailPage from '@/pages/contracts/ContractDetailPage'
import TasksListPage from '@/pages/tasks/TasksListPage'
import TechniciansListPage from '@/pages/technicians/TechniciansListPage'
import TechnicianFormPage from '@/pages/technicians/TechnicianFormPage'
import TechnicianDetailPage from '@/pages/technicians/TechnicianDetailPage'
import SettingsPage from '@/pages/settings/SettingsPage'
import NotificationsPage from '@/pages/notifications/NotificationsPage'
import NotificationPreferencesPage from '@/pages/notifications/NotificationPreferencesPage'
import PrivacyPolicyPage from '@/pages/compliance/PrivacyPolicyPage'
import PrivacyDataPage from '@/pages/compliance/PrivacyDataPage'
import CheckoutSuccessPage from '@/pages/checkout/CheckoutSuccessPage'
import CheckoutCancelPage from '@/pages/checkout/CheckoutCancelPage'
import type { PermissionModule } from '@/types'
import type { ReactNode } from 'react'

function withModule(module: PermissionModule, page: ReactNode) {
  return <ModuleRoute module={module}>{page}</ModuleRoute>
}

function withModuleEdit(module: PermissionModule, page: ReactNode) {
  return (
    <ModuleRoute module={module} action='edit'>
      {page}
    </ModuleRoute>
  )
}

export const router = createBrowserRouter([
  // Public auth routes
  { path: '/login', element: <LoginPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage mode='reset' /> },
  { path: '/set-password', element: <ResetPasswordPage mode='setup' /> },

  // Public Stripe Checkout return pages — the paying client is on their phone
  // browser and NOT authenticated, so these stay OUTSIDE every ProtectedRoute.
  { path: '/checkout/success', element: <CheckoutSuccessPage /> },
  { path: '/checkout/cancel', element: <CheckoutCancelPage /> },

  // Protected portal (Admin / Manager / Operator)
  {
    path: '/',
    element: (
      <ProtectedRoute allowedRoles={['Admin', 'Manager', 'Operator']}>
        <ConsentGate>
          <Layout />
          <CookieConsentBanner />
        </ConsentGate>
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <Navigate to='/dashboard' replace /> },
      {
        path: 'dashboard',
        element: withModule('dashboard', <DashboardPage />),
      },

      // Clients (2.4.x) — /new and /:id/edit deep-link the list + side panel.
      { path: 'clients', element: withModule('clients', <ClientsListPage />) },
      {
        path: 'clients/new',
        element: withModule('clients', <ClientsListPage />),
      },
      {
        path: 'clients/:id/edit',
        element: withModule('clients', <ClientsListPage />),
      },
      {
        path: 'clients/:id',
        element: withModule('clients', <ClientDetailPage />),
      },

      // Agenda / Jobs (2.3.x)
      { path: 'calendar', element: withModule('agenda', <AgendaPage />) },
      {
        path: 'jobs/new',
        element: withModuleEdit('agenda', <JobFormPage mode='create' />),
      },
      { path: 'jobs/:id', element: withModule('agenda', <JobDetailPage />) },
      {
        path: 'jobs/:id/edit',
        element: withModuleEdit('agenda', <JobFormPage mode='edit' />),
      },
      {
        path: 'jobs/:id/report/new',
        element: withModuleEdit(
          'agenda',
          <JobReportWizardPage mode='create' />
        ),
      },
      {
        path: 'jobs/:id/report/edit',
        element: withModuleEdit('agenda', <JobReportWizardPage mode='edit' />),
      },

      // Quotes (2.5.x)
      { path: 'quotes', element: withModule('quotes', <QuotesListPage />) },
      {
        path: 'quotes/new',
        element: withModule('quotes', <QuoteFormPage mode='create' />),
      },
      {
        path: 'quotes/:id',
        element: withModule('quotes', <QuoteDetailPage />),
      },
      {
        path: 'quotes/:id/edit',
        element: withModule('quotes', <QuoteFormPage mode='edit' />),
      },

      // Invoices & Credit notes (2.6.x)
      {
        path: 'invoices',
        element: withModule('invoices', <InvoicesListPage />),
      },
      {
        path: 'invoices/new',
        element: withModule('invoices', <InvoiceFormPage mode='create' />),
      },
      {
        path: 'invoices/:id',
        element: withModule('invoices', <InvoiceDetailPage />),
      },
      {
        path: 'invoices/:id/edit',
        element: withModule('invoices', <InvoiceFormPage mode='edit' />),
      },
      {
        path: 'credit-notes',
        element: withModule('invoices', <CreditNotesListPage />),
      },

      // Payments (2.7.x)
      {
        path: 'payments',
        element: withModule('payments', <PaymentsListPage />),
      },
      {
        path: 'payments/new',
        element: withModule('payments', <PaymentFormPage />),
      },
      {
        path: 'payments/:id',
        element: withModule('payments', <PaymentDetailPage />),
      },

      // Contracts (2.8.x)
      {
        path: 'contracts',
        element: withModule('contracts', <ContractsListPage />),
      },
      {
        path: 'contracts/new',
        element: withModule('contracts', <ContractFormPage mode='create' />),
      },
      {
        path: 'contracts/:id',
        element: withModule('contracts', <ContractDetailPage />),
      },
      {
        path: 'contracts/:id/edit',
        element: withModule('contracts', <ContractFormPage mode='edit' />),
      },

      // Tasks (2.9.x)
      { path: 'tasks', element: withModule('tasks', <TasksListPage />) },
      {
        path: 'tasks/new',
        element: <Navigate to='/tasks?panel=create' replace />,
      },
      { path: 'tasks/:id', element: withModule('tasks', <TasksListPage />) },
      {
        path: 'tasks/:id/edit',
        element: withModule('tasks', <TasksListPage />),
      },

      // Technicians (2.10.x) — gated by technicians.view (not role alone)
      {
        path: 'technicians',
        element: withModule('technicians', <TechniciansListPage />),
      },
      {
        path: 'technicians/new',
        element: withModule(
          'technicians',
          <TechnicianFormPage mode='create' />
        ),
      },
      {
        path: 'technicians/:id',
        element: withModule('technicians', <TechnicianDetailPage />),
      },
      {
        path: 'technicians/:id/edit',
        element: withModule('technicians', <TechnicianFormPage mode='edit' />),
      },

      // Settings (2.11.x) — gated by settings.view; Admin-only writes stay in panels/API
      { path: 'settings', element: withModule('settings', <SettingsPage />) },
      {
        path: 'settings/:section',
        element: withModule('settings', <SettingsPage />),
      },

      // Notifications (2.12.x) - all portal roles (not a gated module on FE)
      { path: 'notifications', element: <NotificationsPage /> },
      {
        path: 'notifications/preferences',
        element: <NotificationPreferencesPage />,
      },

      // Platform compliance (2.13.x)
      { path: 'privacy-policy', element: <PrivacyPolicyPage /> },
      { path: 'privacy-data', element: <PrivacyDataPage /> },
    ],
  },

  {
    path: '*',
    element: (
      <div className='flex min-h-screen items-center justify-center'>
        <NotFound />
      </div>
    ),
  },
])

function NotFound() {
  const { t } = useTranslation('common')
  return <p className='text-muted-foreground'>{t('errors.notFound')}</p>
}

function RouteErrorBoundary() {
  const { t } = useTranslation(['common', 'shell'])
  const error = useRouteError()

  const message = isRouteErrorResponse(error)
    ? error.statusText || error.data?.message
    : error instanceof Error
      ? error.message
      : t('common:messages.error')

  return (
    <div className='flex min-h-screen items-center justify-center bg-[#f4f5f8] px-6'>
      <div className='w-full max-w-lg rounded-3xl border border-[#e7e9ef] bg-white p-8 shadow-[0_1px_4px_-4px_rgba(38,45,66,0.05),0_8px_16px_-4px_rgba(38,45,66,0.1)]'>
        <p className='text-sm font-semibold uppercase tracking-[0.08em] text-[#ff5a1f]'>
          {t('common:messages.error')}
        </p>
        <h1 className='mt-3 text-2xl font-semibold text-[#171a20]'>
          {t('common:messages.error')}
        </h1>
        <p className='mt-3 text-sm text-[#6c7285]'>{message}</p>
        <div className='mt-6 flex flex-wrap gap-3'>
          <Link
            to='/dashboard'
            className='inline-flex h-11 items-center justify-center rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90'
          >
            {t('shell:nav.dashboard')}
          </Link>
          <Link
            to='/technicians'
            className='inline-flex h-11 items-center justify-center rounded-xl border border-[#e7e9ef] bg-white px-5 text-sm font-semibold text-[#171a20] transition hover:bg-[#f8fafd]'
          >
            {t('shell:nav.technicians')}
          </Link>
        </div>
      </div>
    </div>
  )
}
